import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getRandomOrder(maxNum: number) {
  const arr = Array.from({ length: maxNum }, (_, i) => i + 1);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

export function isDenver(location: string) {
  return location.toLowerCase().includes("denver, co");
}

export function calculateMonthlyPayment(
  homePrice: number,
  downPayment: number,
  interestRate = 0.07, // 7% default rate
  loanTermYears = 30,
): number {
  if (homePrice === 0) return 0;

  const principal = homePrice - downPayment;
  const monthlyRate = interestRate / 12;
  const numberOfPayments = loanTermYears * 12;

  // Monthly mortgage payment formula
  const monthlyPayment =
    (principal * (monthlyRate * (1 + monthlyRate) ** numberOfPayments)) /
    ((1 + monthlyRate) ** numberOfPayments - 1);

  // Add estimated property tax (1.1% annually) and insurance ($100/month)
  const monthlyPropertyTax = (homePrice * 0.011) / 12;
  const monthlyInsurance = 100;

  return Math.round(monthlyPayment + monthlyPropertyTax + monthlyInsurance);
}

export function formatLargeNumber(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
}

export const formatCurrency = (
  value: number,
  abbreviate = false,
  hideUSD = false,
) => {
  // Only abbreviate when explicitly requested - remove window check for SSR safety
  if (abbreviate) {
    if (value >= 1_000_000)
      return `${hideUSD ? "" : "$"}${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000)
      return `${hideUSD ? "" : "$"}${(value / 1_000).toFixed(0)}K`;
  }

  // Default standard format
  return new Intl.NumberFormat("en-US", {
    style: hideUSD ? "decimal" : "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatNumber = (value: number) => {
  return new Intl.NumberFormat("en-US").format(value);
};

export async function resizeImage(
  file: File,
  maxWidth: number,
  maxHeight: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], file.name, { type: "image/jpeg" }));
        } else {
          reject(new Error("Canvas to Blob conversion failed"));
        }
      }, "image/jpeg");
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export function slugify(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/**
 * Cleans property listing URLs by removing tracking parameters and normalizing format
 * @param url The URL to clean
 * @returns The cleaned URL
 */
export function cleanPropertyUrl(url: string): string {
  try {
    // Parse the URL
    const parsedUrl = new URL(url);

    // Remove all query parameters
    parsedUrl.search = "";

    // Remove trailing slash if present
    let cleanedUrl = parsedUrl.toString();
    if (cleanedUrl.endsWith("/")) {
      cleanedUrl = cleanedUrl.slice(0, -1);
    }

    // Add back the trailing slash for consistency
    return `${cleanedUrl}/`;
  } catch (error) {
    // If URL parsing fails, return the original URL
    console.error("Error cleaning URL:", error);
    return url;
  }
}

export function toSnakeCase(obj: Record<string, unknown>) {
  const snakeCase: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const snakeKey = key.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`,
    );
    snakeCase[snakeKey] = obj[key];
  }
  return snakeCase;
}

export function cleanHtmlContent(html: string | undefined): string {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, "") // Remove HTML tags
    .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&amp;/g, "&") // Replace &amp; with &
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .trim();
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function formatCompactNumber(value: number): string {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    if (millions >= 10) return `${Math.round(millions)}M`;
    return `${Math.floor(millions)}M+`;
  }
  if (value >= 1_000) {
    const thousands = value / 1_000;
    if (thousands >= 10) return `${Math.round(thousands)}k`;
    return `${Math.floor(thousands)}k+`;
  }
  if (value >= 100) return "99+";
  return value.toString();
}

/**
 * Extracts a readable company/website name from a URL
 */
export function getWebsiteNameFromUrl(url?: string | null): string {
  if (!url) return "Website";
  try {
    // Ensure URL has a protocol
    const urlWithProtocol = url.match(/^https?:\/\//i) ? url : `https://${url}`;
    const urlObj = new URL(urlWithProtocol);
    const hostname = urlObj.hostname;

    // Remove www. and common TLDs
    let name = hostname
      .replace(/^www\./, "")
      .replace(/\.(com|io|co|net|org|app)$/, "");

    // Extract the main part (before the first dot)
    const parts = name.split(".");
    name = parts[0];

    // Remove common prefixes
    const prefixes = ["try", "get", "use", "join", "hey", "go", "with"];
    for (const prefix of prefixes) {
      if (name.startsWith(prefix)) {
        name = name.substring(prefix.length);
        break;
      }
    }

    // Capitalize first letter
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch (e) {
    // If URL parsing fails, try to extract a name from raw string
    const simpleName = url.replace(/^www\./, "").split(".")[0];
    return simpleName.charAt(0).toUpperCase() + simpleName.slice(1);
  }
}

/**
 * Gets a favicon URL for a domain
 */
export function getFaviconUrl(url?: string | null): string | null {
  if (!url) return null;
  try {
    // Ensure URL has a protocol
    const urlWithProtocol = url.match(/^https?:\/\//i) ? url : `https://${url}`;
    const urlObj = new URL(urlWithProtocol);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=128`;
  } catch (e) {
    // If parsing fails, try a simpler approach
    const domain = url.replace(/^https?:\/\//i, "").split("/")[0];
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  }
}
